/* eslint-disable @atlassian/relay/graphql-naming -- previously disabled in `design-system` folder, does not need to be, but to keep things 1:1 for now */
import React, { forwardRef } from 'react';

import { graphql, useFragment } from 'react-relay';

import Avatar, { type AvatarPropTypes } from '@atlaskit/avatar/avatar';
import type { SizeType } from '@atlaskit/avatar/types';

import type { avatarRelay_user$key } from './__generated__/avatarRelay_user.graphql';
import { getAppearanceForAppType } from './utils';

export type AvatarRelayProps = Omit<AvatarPropTypes, 'src' | 'appearance'> & {
	size?: SizeType;
	userFragment: avatarRelay_user$key | null;
};

/**
 * __Avatar relay__
 *
 * Displays a user's profile image with an appearance derived from the user's appType:
 * - circle for users and system
 * - square for teams
 * - hexagon for agents
 *
 * Forwards Avatar props such as label, size, presence, and status.
 */
export const AvatarRelay: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<AvatarRelayProps> & React.RefAttributes<HTMLElement>
> = forwardRef<HTMLElement, AvatarRelayProps>(
	(
		{
			userFragment,
			label,
			borderColor,
			children,
			href,
			isDisabled,
			name,
			presence,
			size,
			status,
			stackIndex,
			tabIndex,
			target,
			onClick,
			testId,
			analyticsContext,
			as,
			imgLoading,
			'aria-controls': ariaControls,
			'aria-expanded': ariaExpanded,
			'aria-haspopup': ariaHasPopup,
		},
		ref,
	) => {
		const user = useFragment(
			graphql`
				fragment avatarRelay_user on User {
					picture
					... on AppUser {
						appType
					}
				}
			`,
			userFragment,
		);
		const appearance = getAppearanceForAppType(user?.appType);

		return (
			<Avatar
				ref={ref}
				// from relay fragment
				src={user?.picture}
				appearance={appearance}
				// prop-drill from AvatarRelayProps
				label={label}
				borderColor={borderColor}
				children={children}
				href={href}
				isDisabled={isDisabled}
				name={name}
				presence={presence}
				size={size}
				status={status}
				stackIndex={stackIndex}
				tabIndex={tabIndex}
				target={target}
				onClick={onClick}
				testId={testId}
				analyticsContext={analyticsContext}
				as={as}
				imgLoading={imgLoading}
				aria-controls={ariaControls}
				aria-expanded={ariaExpanded}
				aria-haspopup={ariaHasPopup}
			/>
		);
	},
);
